/**
 * JoinHandoffContainer — Hand-off screen for the group invite flow
 * Route: /groups/join/:gid/app?inviter=<uid>
 *
 * "Download Splyt to join {group}" — the join completes IN THE APP on next login.
 * - Mobile: Open Splyt button + App Store / Play Store buttons
 * - Desktop: QR code (universal invite link) + store buttons
 */
import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useLocation } from 'react-router-dom';
import QRCode from 'qrcode.react';
import {
  brand,
  fontSans,
  spacing,
  radii,
  shadows,
} from '../../style/splytBrand';
import useJoinPageChrome from './useJoinPageChrome';
import { getGroupInvitePreview } from '../../API/groups';
import { mapPreviewToView, readInviteParams, buildInviteQuery } from './inviteHelpers';
import { track, EVENTS, STORE, SURFACE, DOWNLOAD_SOURCE } from '../../analytics/track';

// ─── Constants ────────────────────────────────────────────────────────────────

const BREAKPOINT = 768;

// iOS store link carries the Group Invite custom product page (ppid), reserved
// for exactly this lane. App Store Connect then reports installs per page, which
// is the install truth Mixpanel cannot see. Same wiring as the receipt surfaces
// (JoinCtaBlock, ReceiptTextAppBanner), which use the Guest CTA page instead.
const APP_STORE_URL = 'https://apps.apple.com/us/app/splyt-tab-bill-splitter/id1605675055?ppid=d5ff7239-dabd-4154-ab43-fc7485518599';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.app.splytpay&hl=en_US';
const ANDROID_PACKAGE = 'com.app.splytpay';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'unknown';
}

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}

// ─── Subcomponents ────────────────────────────────────────────────────────────

function Wordmark({ size = 22, color = brand.caviar }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        fontFamily: fontSans,
        fontWeight: 700,
        fontSize: size,
        letterSpacing: '-0.04em',
        color,
      }}
    >
      <svg width={size * 0.95} height={size * 0.95} viewBox="0 0 24 24" fill="none">
        <path
          d="M6 4h8a4 4 0 010 8H10a4 4 0 010 8h-4"
          stroke={color}
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      splyt
    </div>
  );
}

function GroupAvatarBig({ emoji, accent, size = 96 }) {
  const borderRadius = size * 0.28;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius,
        background: `linear-gradient(135deg, ${accent} 0%, ${accent}AA 60%, #fff 130%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.45,
        boxShadow: `0 14px 36px ${accent}55, 0 2px 4px rgba(0,0,0,0.06)`,
        position: 'relative',
      }}
    >
      {emoji}
      {/* Radial highlight */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius,
          background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.35) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function MemberAvatar({ initial, color, size = 24 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        color: '#fff',
        fontFamily: fontSans,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.44,
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
}

function MemberStack({ people, size = 24, max = 3, ringColor = brand.marsh75 }) {
  const shown = people.slice(0, max);
  return (
    <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
      {shown.map((person, i) => (
        <div
          key={i}
          style={{
            marginLeft: i === shown.length - 1 ? 0 : -size * 0.4,
            border: `2.5px solid ${ringColor}`,
            borderRadius: '50%',
            zIndex: shown.length - i,
          }}
        >
          <MemberAvatar initial={person.initial} color={person.color} size={size} />
        </div>
      ))}
    </div>
  );
}

function CheckBadge({ size = 30, borderColor = brand.marsh }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: brand.positive,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(52,199,89,0.4)',
        border: `3px solid ${borderColor}`,
      }}
    >
      <svg width={size * 0.47} height={size * 0.47} viewBox="0 0 12 12">
        <path
          d="M2 6.5l2.5 2.5L10 3.5"
          stroke="#fff"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    // svh, matching the wrapper below. `100vh` is the LARGE viewport on iOS
    // Safari (toolbars hidden), so it made the document ~80 to 100px taller
    // than the visible area and the page scrolled for no reason.
    minHeight: '100vh',
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    backgroundColor: brand.marsh,
    fontFamily: fontSans,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  // Mobile layout
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    // border-box, or the padding below is added OUTSIDE 100svh: the column
    // became 100svh + 34px, the bottom-aligned footer sat 20px under the fold,
    // and iOS Safari's toolbar cut the "App Store" / "Google Play" line in half.
    // Nothing in this app sets a global border-box (App.css resets margin and
    // padding only, no CssBaseline), so it has to be set here.
    boxSizing: 'border-box',
    minHeight: '100vh',
    // Small viewport height, NOT dvh: iOS Chrome reports dvh as the
    // bars-hidden height, which laid the store badges out behind the bottom
    // toolbar. svh is the bars-visible height, so the footer always fits.
    '@supports (height: 100svh)': {
      minHeight: '100svh',
    },
    padding: `${spacing.md + 4}px ${spacing.md + 4}px calc(14px + env(safe-area-inset-bottom, 0px))`,
  },

  mobileContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    paddingTop: 14,
  },

  mobileFooter: {
    paddingTop: 12,
  },

  // Desktop layout
  desktopWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '24px 40px 40px',
    overflow: 'auto',
  },

  desktopHeader: {
    width: '100%',
    maxWidth: 1080,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },

  desktopCard: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: brand.surface,
    borderRadius: radii.card,
    padding: 36,
    boxShadow: shadows.card,
    border: `1px solid ${brand.marshNeutral}`,
  },

  desktopContent: {
    textAlign: 'center',
  },

  desktopFooter: {
    marginTop: 24,
  },

  // Hero
  heroWrapper: {
    position: 'relative',
    marginBottom: 18,
  },

  checkBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
  },

  // Typography
  title: {
    margin: 0,
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1.1,
    letterSpacing: '-0.025em',
    color: brand.caviar,
  },

  subtitle: {
    margin: '8px 0 0',
    maxWidth: 280,
    fontWeight: 400,
    fontSize: 15,
    lineHeight: 1.45,
    color: brand.caviar75,
    '& strong': {
      fontWeight: 600,
      color: brand.caviar,
    },
  },

  // Balance teaser card (mobile)
  balanceCard: {
    marginTop: 22,
    padding: '14px 18px',
    background: brand.marsh75,
    borderRadius: 14,
    border: `1px solid ${brand.marshNeutral}`,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
  },

  balanceContent: {
    flex: 1,
    textAlign: 'left',
  },

  balanceEyebrow: {
    fontWeight: 500,
    fontSize: 11,
    lineHeight: 1,
    color: brand.caviar50,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  balanceAmount: {
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 1.1,
    color: brand.caviar,
    marginTop: 4,
    fontVariantNumeric: 'tabular-nums',
  },

  owePill: {
    padding: '4px 9px',
    borderRadius: radii.pill,
    background: `${brand.ketchup}1A`,
    fontWeight: 700,
    fontSize: 10,
    lineHeight: 1,
    color: brand.ketchup,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },

  // Primary CTA
  primaryBtn: {
    width: '100%',
    appearance: 'none',
    background: brand.caviar,
    color: brand.surface,
    border: 'none',
    borderRadius: radii.pill,
    padding: '16px 22px',
    fontFamily: fontSans,
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: '-0.005em',
    cursor: 'pointer',
    boxShadow: shadows.primaryButton,
    transition: 'transform 120ms ease',
    '&:active': {
      transform: 'scale(0.985)',
    },
  },

  // Caption under the single Get Splyt button.
  ctaSubline: {
    marginTop: 8,
    textAlign: 'center',
    fontWeight: 500,
    fontSize: 12,
    lineHeight: 1.3,
    color: brand.caviar50,
  },

  // For people who already have the app. Deliberately a quiet text link: on iOS
  // a custom scheme with the app missing shows Safari's "cannot open the page"
  // alert, so only self-selected installed users should reach it.
  openAppLink: {
    display: 'block',
    width: '100%',
    marginTop: 12,
    padding: 0,
    background: 'none',
    border: 'none',
    fontFamily: fontSans,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.3,
    color: brand.blueberry,
    cursor: 'pointer',
  },

  // Store buttons row
  storeRow: {
    display: 'flex',
    gap: 10,
    marginTop: 10,
  },

  storeBtn: {
    flex: 1,
    appearance: 'none',
    background: brand.surface,
    border: `1.5px solid ${brand.marshNeutral}`,
    borderRadius: 14,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'transform 120ms ease',
    '&:active': {
      transform: 'scale(0.985)',
    },
  },

  storeBtnHighlighted: {
    background: brand.caviar,
    borderColor: brand.caviar,
  },

  storeBtnDimmed: {
    opacity: 0.5,
  },

  storeEmoji: {
    fontSize: 22,
    flexShrink: 0,
  },

  storePlayEmoji: {
    fontSize: 18,
    flexShrink: 0,
  },

  storeLabel: {
    textAlign: 'left',
  },

  storeLabelSmall: {
    fontWeight: 500,
    fontSize: 9,
    lineHeight: 1,
    color: brand.caviar50,
  },

  storeLabelSmallLight: {
    color: 'rgba(255,255,255,0.7)',
  },

  storeLabelBig: {
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.2,
    color: brand.caviar,
    marginTop: 2,
  },

  storeLabelBigLight: {
    color: '#fff',
  },

  // QR Card (desktop)
  qrCard: {
    marginTop: 26,
    display: 'flex',
    gap: 22,
    alignItems: 'center',
    padding: 20,
    background: brand.marsh75,
    borderRadius: 20,
    border: `1px solid ${brand.marshNeutral}`,
  },

  qrWrapper: {
    background: '#fff',
    padding: 10,
    borderRadius: 14,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    flexShrink: 0,
  },

  qrMeta: {
    flex: 1,
    textAlign: 'left',
  },

  qrEyebrow: {
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1,
    color: brand.caviar50,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: 8,
  },

  qrSubtitle: {
    fontWeight: 600,
    fontSize: 15,
    lineHeight: 1.3,
    color: brand.caviar,
    marginBottom: 14,
  },

  qrStoreStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },

  qrStoreBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '7px 12px',
    borderRadius: 10,
    background: brand.caviar,
    color: '#fff',
    cursor: 'pointer',
    textDecoration: 'none',
    border: 'none',
    transition: 'transform 120ms ease',
    '&:active': {
      transform: 'scale(0.985)',
    },
  },

  qrStoreLabelSmall: {
    fontWeight: 500,
    fontSize: 8,
    lineHeight: 1,
    color: 'rgba(255,255,255,0.7)',
  },

  qrStoreLabelBig: {
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.2,
    color: '#fff',
    marginTop: 2,
  },




}));

// ─── Main Component ───────────────────────────────────────────────────────────

export default function JoinHandoffContainer() {
  useJoinPageChrome();
  const classes = useStyles();
  const { gid } = useParams();
  const location = useLocation();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < BREAKPOINT;
  const platform = detectPlatform();

  const { inviter, inviteCode, utmSource } = readInviteParams(location.search);
  const query = buildInviteQuery({ inviter, inviteCode, utmSource });

  // Every get-the-app control on this page: the store buttons in both layouts
  // and the Open Splyt deep link, which falls back to a store.
  const trackStoreTap = useCallback((store) => {
    track(EVENTS.APP_DOWNLOAD_CLICKED, {
      store,
      source: DOWNLOAD_SOURCE.GROUP_HANDOFF,
      surface: SURFACE.GROUP,
      group_id: gid,
      invite_id: inviteCode,
    });
  }, [gid, inviteCode]);

  // Fetch the (display-only) group so the hand-off can name the group + show who's in.
  const [view, setView] = useState(null);
  useEffect(() => {
    let active = true;
    getGroupInvitePreview(gid, inviter)
      .then((preview) => {
        if (active) setView(mapPreviewToView(preview));
      })
      .catch((err) => {
        console.error('[JoinHandoff] preview failed:', err);
      });
    return () => {
      active = false;
    };
  }, [gid, inviter]);

  const isLoaded = view && view.status === 'ok';
  const groupName = isLoaded ? view.groupName : 'your group';
  const groupEmoji = isLoaded ? view.emoji : '🧾';
  const groupAccent = isLoaded ? view.accent : '#8C8AFD';
  const members = isLoaded ? view.members : [];

  // Universal invite link — scanning opens the app (if installed) or this landing.
  const qrUrl = `${window.location.origin}/groups/join/${gid}${query}`;

  // The app link, with the invite context attached. The mobile route reads both
  // `inviter` and `i`, so a warm open attributes the join to whoever actually
  // invited them instead of falling back to the group's creator.
  const appLink = `splyt://groups/join/${gid}${query}`;

  // Primary action: get the app. On iOS this is a plain App Store navigation —
  // an installed iPhone user never reaches this page, because their tap on the
  // invite text opens the app through the universal link. On Android there are
  // no app links yet, so installed users DO land here: an intent URL opens Splyt
  // when it is installed and falls back to Play when it is not, in one tap, with
  // no timer and no "cannot open the page" alert.
  const handleGetSplyt = useCallback(() => {
    if (platform === 'ios') {
      trackStoreTap(STORE.IOS);
      window.location.href = APP_STORE_URL;
      return;
    }
    if (platform === 'android') {
      trackStoreTap(STORE.ANDROID);
      const fallback = encodeURIComponent(PLAY_STORE_URL);
      window.location.href =
        `intent://groups/join/${gid}${query}#Intent;scheme=splyt;package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
      return;
    }
    // Unknown platform: the store buttons are rendered instead, so this is unreachable.
  }, [gid, query, platform, trackStoreTap]);

  // Secondary, for people who already have Splyt. Kept OFF the primary button:
  // on iOS a custom scheme for an app that is not installed shows Safari's
  // "cannot open the page" alert, so only self-selected installed users see it.
  const handleOpenApp = useCallback(() => {
    trackStoreTap(platform === 'ios' ? STORE.IOS : STORE.ANDROID);
    window.location.href = appLink;
  }, [appLink, platform, trackStoreTap]);

  // Render hero section (shared between mobile and desktop)
  const renderHero = (avatarSize = 96, badgeBorderColor = brand.marsh) => (
    <div className={classes.heroWrapper}>
      <GroupAvatarBig emoji={groupEmoji} accent={groupAccent} size={avatarSize} />
      <div className={classes.checkBadge}>
        <CheckBadge size={avatarSize === 96 ? 30 : 28} borderColor={badgeBorderColor} />
      </div>
    </div>
  );

  // Mobile layout
  if (isMobile) {
    const isIOS = platform === 'ios';
    const isKnownPlatform = platform === 'ios' || platform === 'android';

    return (
      <div className={classes.root}>
        <div className={classes.mobileWrapper}>
          <div className={classes.mobileContent}>
            {renderHero(96, brand.marsh)}

            <h1 className={classes.title}>You&apos;re almost in</h1>
            <p className={classes.subtitle}>
              Download Splyt to join <strong>{groupName}</strong>. Log in with this
              same number and you&apos;ll land right in the group.
            </p>

            {/* Who's already in */}
            {members.length > 0 && (
              <div className={classes.balanceCard}>
                <MemberStack
                  people={members}
                  size={24}
                  max={3}
                  ringColor={brand.marsh75}
                />
                <div className={classes.balanceContent}>
                  <div className={classes.balanceEyebrow}>Already in</div>
                  <div className={classes.balanceAmount}>
                    {view.memberCount}{' '}
                    {view.memberCount === 1 ? 'person' : 'people'}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={classes.mobileFooter}>
            {isKnownPlatform ? (
              <>
                <button className={classes.primaryBtn} onClick={handleGetSplyt}>
                  Get Splyt
                </button>
                <div className={classes.ctaSubline}>
                  {isIOS ? 'Free on the App Store' : 'Free on Google Play'}
                </div>
                <button type="button" className={classes.openAppLink} onClick={handleOpenApp}>
                  Already have Splyt? Open it
                </button>
              </>
            ) : (
              /* Desktop-class user agent on a narrow window: no store to route
                 to, so show both badges and let them choose. */
              <div className={classes.storeRow}>
                <a
                  href={APP_STORE_URL}
                  className={classes.storeBtn}
                  onClick={() => trackStoreTap(STORE.IOS)}
                >
                  <span className={classes.storeEmoji}>🍎</span>
                  <div className={classes.storeLabel}>
                    <div className={classes.storeLabelSmall}>Download on the</div>
                    <div className={classes.storeLabelBig}>App Store</div>
                  </div>
                </a>
                <a
                  href={PLAY_STORE_URL}
                  className={classes.storeBtn}
                  onClick={() => trackStoreTap(STORE.ANDROID)}
                >
                  <span className={classes.storePlayEmoji}>▶︎</span>
                  <div className={classes.storeLabel}>
                    <div className={classes.storeLabelSmall}>Get it on</div>
                    <div className={classes.storeLabelBig}>Google Play</div>
                  </div>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout
  return (
    <div className={classes.root}>
      <div className={classes.desktopWrapper}>
        <div className={classes.desktopHeader}>
          <Wordmark size={22} />
        </div>

        <div className={classes.desktopCard}>
          <div className={classes.desktopContent}>
            {renderHero(84, brand.surface)}

            <h1 className={classes.title}>You&apos;re almost in</h1>
            <p className={classes.subtitle} style={{ maxWidth: 'none' }}>
              Splyt is mobile-only. Scan to download and join{' '}
              <strong>{groupName}</strong> on your phone.
            </p>

            {/* QR Card */}
            <div className={classes.qrCard}>
              <div className={classes.qrWrapper}>
                <QRCode value={qrUrl} size={140} level="M" />
              </div>
              <div className={classes.qrMeta}>
                <div className={classes.qrEyebrow}>Scan with your camera</div>
                <div className={classes.qrSubtitle}>Opens Splyt on iOS or Android</div>
                <div className={classes.qrStoreStack}>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.qrStoreBtn}
                    onClick={() => trackStoreTap(STORE.IOS)}
                  >
                    <span style={{ fontSize: 16 }}>🍎</span>
                    <div>
                      <div className={classes.qrStoreLabelSmall}>App Store</div>
                      <div className={classes.qrStoreLabelBig}>Download iOS</div>
                    </div>
                  </a>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.qrStoreBtn}
                    onClick={() => trackStoreTap(STORE.ANDROID)}
                  >
                    <span style={{ fontSize: 14 }}>▶︎</span>
                    <div>
                      <div className={classes.qrStoreLabelSmall}>Google Play</div>
                      <div className={classes.qrStoreLabelBig}>Download Android</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
